import {InvalidArgumentException} from "../../errors/Exceptions.js";
import {isString} from "../../utils.js";


/**
 * Class for Turing machine alphabet
 */
export class TmAlphabet{
    #chars;         // string[], sorted (asc), contains emptyCellChar
    #emptyCellChar; // string

    /**
     * Initializes Alphabet. If given <code>chars</code> does not contain <code>emptyCellChar</code>, pushes the latter to it. .
     * @param chars {string[]} is non-sparse array of characters of alphabet. Character is string with length=1.
     * Duplicate chars are not considered and will be added to alphabet only once.
     * @param emptyCellChar {string} is character that denotes empty cells.
     */
    constructor(chars, emptyCellChar) {
        if(!Array.isArray(chars)){
            throw new InvalidArgumentException("chars must be an Array");
        }
        if(chars.length <= 0){
            throw new InvalidArgumentException("chars array can't be empty");
        }

        // init this.#chars array
        this.#chars = [];
        for(let i = 0; i < chars.length; i++){ // also checks for empty elements
            if(!isString(chars[i])){
                throw new InvalidArgumentException(`each element of chars array must be a string (caused by index: ${i})`);
            }
            if(chars[i].length !== 1){
                throw new InvalidArgumentException(`each element of chars array must have length 1 (caused by index: ${i})`);
            }

            if(!this.#chars.includes(chars[i])){
                this.#chars.push(chars[i]);
            }
        }
        this.#chars.sort(); // to be able to compare chars arrays of different alphabets simply element by element

        // init this.#emptyCellChar
        if(!isString(emptyCellChar)){
            throw new InvalidArgumentException("emptyCellChar must be a string");
        }
        if(emptyCellChar.length !== 1){
            throw new InvalidArgumentException("emptyCellChar must have length 1");
        }

        if(!this.#chars.includes(emptyCellChar)){
            this.#chars.push(emptyCellChar);
        }
        this.#emptyCellChar = emptyCellChar;
    }

    /**
     *
     * @return {string[]}
     */
    get chars(){
        return [...this.#chars]; // copy not to have ability to change this.#chars from outside
    }

    /**
     *
     * @return {string}
     */
    get emptyCellChar(){
        return this.#emptyCellChar;
    }

    /**
     * Checks if alphabet contains given <code>char</code>
     * @param char {string}
     * @return {boolean}
     */
    includes(char){
        if(!isString(char)){
            throw new InvalidArgumentException("char must be a string");
        }

        return this.#chars.includes(char);
    }

    /**
     * Checks if alphabet includes all elements of given <code>chars</code> array.
     * @param chars {string[]} can't be empty. Empty elements of array are ignored
     * @return {boolean}
     */
    includesAll(chars){
        if(!Array.isArray(chars)){
            throw new InvalidArgumentException("chars must be an Array");
        }
        if(chars.length <= 0){
            throw new InvalidArgumentException("chars array can't be empty");
        }
        chars.forEach((char, i) => {
            if(!isString(char)){
                throw new InvalidArgumentException(`each element of chars array must be a string (caused by index: ${i})`);
            }
        });

        return chars.every(char => this.#chars.includes(char));
    }

    /**
     * Compares two <code>TmAlphabet</code> objects (by all properties).
     * The order of chars doesn't matter.
     * @param other {TmAlphabet}
     * @return {boolean}
     */
    equals(other){
        if(!(other instanceof TmAlphabet)){
            return false;
        }

        if(this === other){
            return true;
        }

        return this.emptyCellChar === other.emptyCellChar &&
            this.chars.length === other.chars.length &&
            this.chars.every((char, idx) => char === other.chars[idx]);
        // chars is sorted, so we can compare element by element
    }

    /**
     *
     * @return {string}
     */
    toString(){
        return `chars: [${this.#chars}] (${this.#chars.length} chars), ` +
            `emptyCellChar: ${this.#emptyCellChar}`;
    }
}